import React, { useRef, useState } from 'react';
import { useTheme } from '../ThemeContext';

const GEN_META = {
  greatest: { title: 'Greatest Generation', years: '1901 — 1927', sub: 'Macchina da scrivere, radio a valvole, il mondo prima della luce' },
  silent: { title: 'Silent Generation', years: '1928 — 1945', sub: 'Vespa, FIAT 500, il miracolo economico italiano' },
  boomer: { title: 'Baby Boomer', years: '1946 — 1964', sub: 'Vinile, Walkman, VHS e le proteste del \'68' },
  x: { title: 'Generazione X', years: '1965 — 1980', sub: 'Commodore 64, Game Boy, il Muro di Berlino che cade' },
  millennials: { title: 'Millennials', years: '1981 — 1996', sub: 'Giradischi, musicassette, VHS, web, MP3, smartphone' },
  z: { title: 'Generazione Z', years: '1997 — 2012', sub: 'Smartphone, TikTok, ring light, content creation' },
  alpha: { title: 'Gen Alpha', years: '2013 — 2024', sub: 'iPad, robot, VR, AI generativa' },
  beta: { title: 'Gen Beta', years: '2025 — oggi', sub: 'I primi nati nell\'era dell\'intelligenza artificiale' },
};

export default function GenerationReel({ generation = 'millennials' }) {
  const { isInstitutional } = useTheme();
  const isDark = !isInstitutional;
  const accent = isDark ? '#00FF41' : '#C4532D';
  const meta = GEN_META[generation] || GEN_META.millennials;
  const videoSrc = `/videos/reel_${generation}.mp4`;

  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ended, setEnded] = useState(false);

  const play = () => {
    setStarted(true);
    setEnded(false);
    setProgress(0);
    // Video will autoplay via autoPlay attribute once src is set
  };

  return (
    <div className="relative rounded-xl overflow-hidden border" style={{ borderColor: accent + '33' }} data-testid={`generation-reel-${generation}`}>
      <div className="relative aspect-video bg-black overflow-hidden">
        {started ? (
          <video
            ref={videoRef}
            src={videoSrc}
            className="w-full h-full object-cover"
            playsInline
            muted
            autoPlay
            onTimeUpdate={() => {
              if (videoRef.current && videoRef.current.duration) {
                setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
              }
            }}
            onEnded={() => { setEnded(true); setProgress(100); }}
            style={{ filter: 'brightness(0.85)' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, #0a0a0a 0%, ${accent}08 100%)` }}>
            <p className="font-mono text-sm font-bold opacity-40" style={{ color: accent }}>{meta.years}</p>
          </div>
        )}

        {/* Play overlay — show before start and after end */}
        {(!started || ended) && (
          <button
            onClick={play}
            data-testid={`reel-play-btn-${generation}`}
            className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer group"
            style={{ background: 'rgba(0,0,0,0.4)' }}
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: accent, boxShadow: `0 0 30px ${accent}55` }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill={isDark ? '#000' : '#fff'}><path d="M8 5v14l11-7z"/></svg>
            </div>
          </button>
        )}

        {/* Gradient info overlay */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-4 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>{meta.years}</p>
          <p className="font-black text-lg md:text-xl text-white">{meta.title}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-black/50 relative">
        <div className="h-full transition-all duration-200" style={{ width: `${progress}%`, background: accent }} />
      </div>

      {/* Title bar */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: isDark ? '#0a0a0a' : '#F5F3EE' }}>
        <div>
          <p className="font-mono font-bold text-sm" style={{ color: accent }}>{meta.title}</p>
          <p className={`text-[10px] ${isDark ? 'text-zinc-500' : 'text-[#8A7A62]'}`}>{meta.sub}</p>
        </div>
        <button onClick={play} className="font-mono text-[10px] px-3 py-1 rounded-full border transition-colors" style={{ borderColor: accent + '44', color: accent }}>
          {started && !ended ? 'In riproduzione...' : 'Riproduci'}
        </button>
      </div>
    </div>
  );
}
